import { atom, useAtom, useAtomValue } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { accountService } from '@/services/account.service';
import { queryClient } from '@/lib/query-client';
import type {
  AccountDetail,
  FeatureGuards,
  AiInsights,
  UpdateAccountDto
} from '@/types';

export const selectedAccountIdAtom = atom<number | null>(null);

const accountDetailQueryAtom = atomWithQuery((get) => {
  const id = get(selectedAccountIdAtom);
  return {
    queryKey: ['account', id],
    enabled: id != null,
    queryFn: () => accountService.get(id as number)
  };
});

const activityQueryAtom = atomWithQuery((get) => {
  const id = get(selectedAccountIdAtom);
  return {
    queryKey: ['account-activity', id],
    enabled: id != null,
    queryFn: () => accountService.activity(id as number)
  };
});

export const useSelectedAccountId = () => useAtom(selectedAccountIdAtom);

export const useAccountDetail = () => {
  const [result] = useAtom(accountDetailQueryAtom);
  return result;
};

export const useAccountActivity = () => {
  const [result] = useAtom(activityQueryAtom);
  return result;
};

const invalidate = (id: number) => {
  queryClient.invalidateQueries({ queryKey: ['account', id] });
  queryClient.invalidateQueries({ queryKey: ['account-activity', id] });
  queryClient.invalidateQueries({ queryKey: ['accounts'] });
};

export const useAccountMutations = () => {
  const id = useAtomValue(selectedAccountIdAtom);

  const requireId = (): number => {
    if (id == null) throw new Error('No account selected');
    return id;
  };

  return {
    /** The guards one scope reads, with the keys that scope owns. */
    getFeatureGuards: async (buId: number | null) => {
      const accountId = requireId();
      return accountService.getFeatureGuards(accountId, buId);
    },
    resetFeatureGuards: async (buId: number, keys?: string[]) => {
      const accountId = requireId();
      const res = await accountService.resetFeatureGuards(accountId, buId, keys);
      invalidate(accountId);
      return res;
    },
    updateAccount: async (payload: UpdateAccountDto): Promise<AccountDetail> => {
      const accountId = requireId();
      await accountService.update(accountId, payload);
      invalidate(accountId);
      return accountService.get(accountId);
    },
    updateFeatureGuards: async (patch: Partial<FeatureGuards>, buId: number | null = null) => {
      const accountId = requireId();
      const res = await accountService.updateFeatureGuards(accountId, patch, buId);
      invalidate(accountId);
      return res;
    },
    updateAiInsights: async (patch: Partial<AiInsights>) => {
      const accountId = requireId();
      const res = await accountService.updateAiInsights(accountId, patch);
      invalidate(accountId);
      return res;
    },
    updateProduct: async (
      product: string,
      patch: { enabled?: boolean; max_ticket_fetch?: number }
    ) => {
      const accountId = requireId();
      const res = await accountService.updateProduct(accountId, product, patch);
      invalidate(accountId);
      return res;
    }
  };
};
